/**
 * Org structure — finding a unit.
 *
 * Dimming the non-matches is only useful for the units already on screen. It
 * cannot tell you how many matched, cannot reach a match inside a folded
 * branch, and offers no way to get from the first match to the second except
 * panning a greyed-out canvas looking for a card that is not dim. So the search
 * box owns a result list: it searches the whole tree regardless of what is
 * folded, says how many answered, and takes you to any one of them.
 *
 * Typing deliberately does not move the chart. The camera moves when you pick a
 * result — jumping the canvas on every keystroke loses the place you were
 * looking at, which is the thing you were searching from.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './org-icons';
import { headColour, nf, searchUnits, type OrgModel, type OrgNode, type SearchHit } from './org-model';
import styles from './org-structure.module.css';

/** Rendered rows. Past this the list stops being scannable and the answer is a
 *  better query, not more scrolling — so we say what was left out. */
const MAX_ROWS = 40;

/** The matched run, marked. Everything is a plain substring match, so one split does it. */
const Marked = ({ text, q }: { text: string; q: string }) => {
  const i = q ? text.toLowerCase().indexOf(q) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className={styles.mark}>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

interface SearchProps {
  model: OrgModel;
  /** Raw input value — undebounced, so typing stays responsive. */
  value: string;
  onChange: (v: string) => void;
  /** Debounced, lower-cased, trimmed. */
  query: string;
  vacantOnly: boolean;
  /** Take me to this unit. */
  onPick: (id: string) => void;
  compareSet: string[];
  onToggleCompare: (id: string) => void;
  /** Wording for the primary action, which differs by view. */
  pickVerb: string;
}

const OrgSearch = ({
  model,
  value,
  onChange,
  query,
  vacantOnly,
  onPick,
  compareSet,
  onToggleCompare,
  pickVerb,
}: SearchProps) => {
  // If the box appears with a query already in it, the matches are the helpful
  // thing to show — not an empty popover waiting for another keystroke.
  const [open, setOpen] = useState(() => !!query);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hits = useMemo(() => searchUnits(model, query, vacantOnly), [model, query, vacantOnly]);
  const shown = hits.slice(0, MAX_ROWS);

  // A new query is a new list; keeping the old cursor would point at a row that
  // is no longer there.
  useEffect(() => {
    setActive(0);
    if (query) setOpen(true);
  }, [query]);

  // Click anywhere else and the list goes away, like any other popover.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  // Keep the keyboard cursor in view when it walks past the fold.
  useEffect(() => {
    listRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (id: string) => {
    onPick(id);
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Escape also leaves the CSS full-screen fallback. Closing this popover
      // is the nearer intent, so it consumes the key rather than doing both.
      e.stopPropagation();
      if (open) setOpen(false);
      else onChange('');
      return;
    }
    if (!shown.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % shown.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i - 1 + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(shown[Math.min(active, shown.length - 1)].node.id);
    }
  };

  const fullTrail = (n: OrgNode) => n.path.map((p) => model.byId[p].name).join(' › ');
  /**
   * The nearest two ancestors, with the front elided.
   *
   * What separates two units of the same name is the parent right above them,
   * and that sits at the END of the lineage — so a plain ellipsis would eat
   * precisely the part you are reading the line for. Trimming from the front
   * keeps it. The whole lineage is on the row's title for the rest.
   */
  const trail = (n: OrgNode) => {
    const names = n.path.map((p) => model.byId[p].name);
    return names.length > 2 ? `… › ${names.slice(-2).join(' › ')}` : names.join(' › ');
  };

  const via = (h: SearchHit) => {
    const n = h.node;
    if (h.via === 'officer' && n.head) return <>Officer <Marked text={n.head.name} q={query} /></>;
    if (h.via === 'code') return <><Marked text={n.code} q={query} /> · {n.typeLabel}</>;
    if (h.via === 'district' && n.districtName) return <>District <Marked text={n.districtName} q={query} /></>;
    if (h.via === 'type') return <>{n.code} · <Marked text={n.typeLabel} q={query} /></>;
    return <>{n.code} · {n.typeLabel}</>;
  };

  return (
    <div className={styles.srchwrap} ref={boxRef}>
      <span className={styles.srch}>
        <Icon name="search" className={styles.ic} />
        <input
          ref={inputRef}
          value={value}
          placeholder="Search unit, code, officer or district"
          aria-label="Search units"
          role="combobox"
          aria-expanded={open}
          aria-controls="org-search-results"
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query && (
          <>
            <span className={styles.srchcount}>{nf(hits.length)}</span>
            <button
              type="button"
              className={styles.srchclear}
              aria-label="Clear the search"
              title="Clear the search"
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
            >
              <Icon name="x" className={styles.ic} size={12} />
            </button>
          </>
        )}
      </span>

      {open && query && (
        <div className={styles.results} id="org-search-results" role="listbox" ref={listRef}>
          {hits.length === 0 ? (
            <p className={styles.resnone}>
              No unit matches <b>{value.trim()}</b>.
              {vacantOnly && ' Vacant only is on — turn it off to search every unit.'}
            </p>
          ) : (
            <>
              <p className={styles.reshead}>
                {nf(hits.length)} {hits.length === 1 ? 'unit' : 'units'} match · ↑↓ to move, Enter to {pickVerb}
              </p>
              {shown.map((h, i) => {
                const n = h.node;
                const stored = compareSet.includes(n.id);
                return (
                  <div
                    key={n.id}
                    data-i={i}
                    role="option"
                    aria-selected={i === active}
                    className={`${styles.res}${i === active ? ` ${styles.resactive}` : ''}`}
                    onMouseEnter={() => setActive(i)}
                  >
                    <button type="button" className={styles.resmain} onClick={() => pick(n.id)}>
                      <span
                        className={styles.sq}
                        style={{ background: headColour(n) }}
                      />
                      <span className={styles.restx}>
                        <span className={styles.resnm}>
                          <Marked text={n.name} q={query} />
                        </span>
                        <span className={styles.resmeta}>{via(h)}</span>
                        {n.path.length > 0 && (
                          <span className={styles.respath} title={fullTrail(n)}>
                            {trail(n)}
                          </span>
                        )}
                      </span>
                      <span className={styles.resn}>
                        <i>People</i>
                        {nf(n.people)}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`${styles.pin}${stored ? ` ${styles.pinon}` : ''}`}
                      title={stored ? 'Remove from the compare set' : 'Add to the compare set'}
                      aria-label={stored ? 'Remove from the compare set' : 'Add to the compare set'}
                      onClick={() => onToggleCompare(n.id)}
                    >
                      {stored ? '✓' : '+'}
                    </button>
                  </div>
                );
              })}
              {hits.length > MAX_ROWS && (
                <p className={styles.resmore}>
                  Showing the closest {MAX_ROWS} of {nf(hits.length)}. Add the unit code or the officer’s name to narrow it.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OrgSearch;
